import React, { useState, useEffect } from 'react';
import { Monitor, Link } from 'lucide-react';
import { WebviewCommands } from '../hooks/useWebviewMessaging';
import { Button } from './Button/Button';
import { Icon } from './Icon/Icon';
import { IconName } from './Icon/Icon.type';
import { Text } from './Text/Text';
import { Container } from './Container/Container';

interface WebviewToolbarProps {
  currentUrl: string;
  canGoBack: boolean;
  canGoForward: boolean;
  isLoading?: boolean;
  commands: WebviewCommands;
  initialUrl?: string;
  onUrlChange?: (url: string) => void;
  onLogsClick?: () => void;
  onScreenSizeClick?: () => void;
  onClose?: () => void;
  className?: string;
}

// Utility function to parse URL and extract protocol, domain, and path
const parseUrl = (url: string): { protocol: string; domain: string; port: string; path: string; full: string } => {
  try {
    const urlObj = new URL(url);
    return {
      protocol: urlObj.protocol + '//',
      domain: urlObj.hostname,
      port: urlObj.port ? `:${urlObj.port}` : '',
      path: urlObj.pathname + urlObj.search + urlObj.hash,
      full: url,
    };
  } catch {
    return { protocol: 'https://', domain: url, port: '', path: '', full: url };
  }
};

export const WebviewToolbar: React.FC<WebviewToolbarProps> = ({
  currentUrl,
  canGoBack,
  canGoForward,
  isLoading = false,
  commands,
  initialUrl,
  onUrlChange,
  onLogsClick,
  onScreenSizeClick,
  onClose,
  className = '',
}) => {
  const [pathInput, setPathInput] = useState('');

  const urlParts = parseUrl(currentUrl || '');

  // Update path input when URL changes (includes pathname, query params, and fragments)
  useEffect(() => {
    if (currentUrl) {
      const parsed = parseUrl(currentUrl);
      // parsed.path already includes pathname + search (query params) + hash (fragments)
      setPathInput(parsed.path);
    }
  }, [currentUrl]);

  // Handles form submission when user presses Enter in the path input
  const handlePathSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newPath = pathInput.startsWith('/') ? pathInput : `/${pathInput}`;
    const targetUrl = urlParts.protocol + urlParts.domain + urlParts.port + newPath;
    
    if (onUrlChange) {
      onUrlChange(targetUrl);
    }
    commands.navigate(targetUrl);
  };

  const handlePathChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPathInput(e.target.value);
  };

  const handleDomainClick = () => {
    const pathInputElement = document.querySelector('input[data-path-input]') as HTMLInputElement;
    if (pathInputElement) {
      pathInputElement.focus();
    }
  };

  const handleHomeClick = () => {
    if (initialUrl) {
      if (onUrlChange) {
        onUrlChange(initialUrl);
      }
      commands.navigate(initialUrl);
    }
  };

  return (
    <Container
      variant="primary"
      padding="none"
      className={`w-full flex items-center gap-bg-v2-xs px-bg-v2-xl py-bg-v2-sm border-b border-border-secondary ${className}`}
    >
      {/* Navigation Controls */}
      <div className="flex items-center gap-bg-v2-xs">
        <Button
          label={<Icon name={IconName.ArrowLeft} size="xs" variant="secondary" />}
          onClick={commands.back}
          disabled={!canGoBack}
          variant="ghost"
          size="very-small"
          className="!w-4 !h-6 !p-0 !min-w-0"
          title="Back"
          tooltipPosition="bottom"
        />
        <Button
          label={<Icon name={IconName.ArrowRight} size="sm" variant="secondary" />}
          onClick={commands.forward}
          disabled={!canGoForward}
          variant="ghost"
          size="very-small"
          className="!w-6 !h-6 !p-0 !min-w-0"
          title="Forward"
          tooltipPosition="bottom"
        />
        <Button
          label={
            <Icon
              name={IconName.Reload}
              size="sm"
              variant="secondary"
              className={isLoading ? 'animate-spin' : ''}
            />
          }
          onClick={commands.reload}
          variant="ghost"
          size="very-small"
          className="!w-6 !h-6 !p-0 !min-w-0"
          title="Refresh"
          tooltipPosition="bottom"
        />
        {initialUrl && (
          <Button
            label={<Icon name={IconName.Home} size="sm" variant="secondary" />}
            onClick={handleHomeClick}
            variant="ghost"
            size="very-small"
            className="!w-6 !h-6 !p-0 !min-w-0"
            title="Home"
            tooltipPosition="bottom"
          />
        )}
      </div>

      {/* URL Input Area - Replit Style: Domain Button + Path Input */}
      <form className="flex-1 flex items-center min-w-0 mx-bg-v2-md" onSubmit={handlePathSubmit}>
        <div className="flex-1 flex items-center bg-bg-secondary border border-border-primary rounded-bg-v2-sm focus-within:border-border-brand focus-within:ring-1 focus-within:ring-focus-ring transition-all overflow-hidden">
          {/* Lock Icon */}
          <div className="px-bg-v2-md flex-shrink-0" title="External navigation is not tracked">
            <Icon name={IconName.Lock} size="xs" variant="secondary" />
          </div>

          {/* Domain Button */}
          <Button
            label={
              <div className="flex items-center gap-bg-v2-sm">
                <Link size={12} className="text-text-tertiary" />
                <Text variant="text-xs" weight="medium" color="secondary" as="span">
                  {urlParts.domain + (urlParts.port ? `${urlParts.port}` : '')}
                </Text>
              </div>
            }
            onClick={handleDomainClick}
            type="button"
            variant="ghost"
            size="very-small"
            className="!px-bg-v2-md !py-bg-v2-sm !h-auto !rounded-none border-r border-border-secondary flex-shrink-0"
            aria-label="Domain"
          />
          

          {/* Path Input */}
          <input
            type="text"
            data-path-input
            value={pathInput}
            onChange={handlePathChange}
            className="flex-1 min-w-0 text-bg-v2-sm bg-transparent border-none outline-none px-bg-v2-md py-bg-v2-sm text-text-primary placeholder:text-text-quaternary font-bg-v2-sans font-bg-v2-regular"
            placeholder="/"
            spellCheck={false}
            autoComplete="off"
            autoCapitalize="off"
            autoCorrect="off"
            aria-label="Preview URL path"
          />
        </div>
      </form>

      {/* Right Actions */}
      <div className="flex items-center gap-bg-v2-xs">
        {onLogsClick && (
          <Button
            label={<Icon name={IconName.Document} size="sm" variant="secondary" />}
            onClick={onLogsClick}
            variant="ghost"
            size="very-small"
            className="!w-6 !h-6 !p-0 !min-w-0"
            title="Webview logs"
            tooltipPosition="bottom"
          />
        )}
        {onScreenSizeClick && (
          <Button
            label={<Monitor size={16} className="text-text-secondary" />}
            onClick={onScreenSizeClick}
            variant="ghost"
            size="very-small"
            className="!w-6 !h-6 !p-0 !min-w-0"
            title="Screen size"
            tooltipPosition="bottom"
          />
        )}
        <a
          href={currentUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="w-6 h-6 flex items-center justify-center rounded-bg-v2-md hover:bg-bg-secondary active:bg-bg-tertiary text-text-secondary transition-colors"
          aria-label="New tab"
          title="New tab"
        >
          <Icon name={IconName.ExternalLink} size="sm" variant="secondary" />
        </a>
        {onClose && (
          <Button
            label={<Icon name={IconName.XClose} size="sm" variant="secondary" />}
            onClick={onClose}
            variant="ghost"
            size="very-small"
            className="!w-6 !h-6 !p-0 !min-w-0"
            title="Close Preview"
            tooltipPosition="bottom"
          />
        )}
      </div>
    </Container>
  );
};

